/**
 * Extracts display initials from a person's name.
 *
 * Uses a language-agnostic algorithm: takes the first character of the first
 * word and the first character of the last word. This naturally skips middle
 * particles in any language (Dutch "van der", German "von", French "de la",
 * Spanish "de los", Arabic "bin", etc.) without maintaining prefix lists.
 *
 * @example
 * getInitials("Joost van der Zwan")              // "JZ"
 * getInitials("Wouter van Nierop")               // "WN"
 * getInitials("Marie de la Cruz")                // "MC"
 * getInitials("Ludwig von Beethoven")            // "LB"
 * getInitials("Joost", "van der Zwan")           // "JZ"
 * getInitials("Joost van der Zwan", "")          // "JZ"  (full name in firstName, empty lastName)
 * getInitials("Matej G.", "")                    // "MG"  (full name in firstName, empty lastName)
 * getInitials("Madonna")                         // "M"
 * getInitials("")                                // "?"
 */

/** Extract initials from a full name string. */
export function getInitials(fullName: string): string;
/** Extract initials from separate first and last name strings. */
export function getInitials(firstName: string, lastName: string): string;
export function getInitials(firstOrFull: string, lastName?: string): string {
  if (lastName !== undefined) {
    return getInitialsFromParts(firstOrFull, lastName);
  }
  return getInitialsFromFullName(firstOrFull);
}

/**
 * Extract initials from a full name string.
 * Takes the first character of the first word and the first character of the last word.
 */
function getInitialsFromFullName(fullName: string): string {
  const words = toWords(fullName);

  if (words.length === 0) return '?';
  if (words.length === 1) return words[0][0].toUpperCase();

  const first = words[0][0];
  const last = words[words.length - 1][0];

  return `${first}${last}`.toUpperCase();
}

/**
 * Extract initials from separate first and last name strings.
 * For lastName, takes the last word to skip any prefixes/particles.
 *
 * Handles a common API data quality issue: when the full name is stored
 * entirely in firstName with an empty lastName (e.g. firstName="Joost van der Zwan",
 * lastName=""), falls back to treating firstName as a full name so we still
 * get "JZ" instead of just "J".
 */
function getInitialsFromParts(firstName: string, lastName: string): string {
  const firstWords = toWords(firstName);
  const lastWords = toWords(lastName);

  // If lastName is empty but firstName contains multiple words, the API likely
  // stored the full name in firstName. Fall back to the full-name algorithm.
  if (lastWords.length === 0 && firstWords.length > 1) {
    return getInitialsFromFullName(firstName);
  }

  const first = firstWords.length > 0 ? firstWords[0][0] : '';
  const last = lastWords.length > 0 ? lastWords[lastWords.length - 1][0] : '';

  if (first && last) return `${first}${last}`.toUpperCase();
  if (first) return first.toUpperCase();
  if (last) return last.toUpperCase();

  return '?';
}

/** Split a string into non-empty trimmed words. */
function toWords(value: string): string[] {
  if (!value) return [];
  return value.trim().split(/\s+/).filter(Boolean);
}
